/**
 * /docs/faq — short Q/A. One <details> per question.
 */

import type { ReactNode } from 'react';
import { PageHeader } from '../../components/ui.js';
import { DocsTabs } from '../../components/DocsTabs.js';
import { Card } from '../../components/ui.js';
import { IconChevronRight } from '../../components/Icons.js';

interface Q {
  q: string;
  a: ReactNode;
}

const QUESTIONS: Q[] = [
  {
    q: 'Who can see the apps I build?',
    a: (
      <p>
        By default, anyone at Mytra (workspace visibility). On the app&apos;s{' '}
        <strong>Access</strong> tab, flip to <em>Private</em> and add specific emails &mdash; only
        they and the managers can use it. Anyone you add as a co-manager can deploy, configure
        access, and rotate secrets too.
      </p>
    ),
  },
  {
    q: 'How do I add an API key or password?',
    a: (
      <p>
        Never in chat. Go to <strong>your app &rarr; Secrets</strong>, click <em>Add secret</em>,
        give it an UPPER_SNAKE name (e.g. <span className="font-mono text-xs">STRIPE_API_KEY</span>
        ), paste the value, save. Then tell the AI:{' '}
        <em>
          &ldquo;use <span className="font-mono text-xs">STRIPE_API_KEY</span> for the Stripe
          calls.&rdquo;
        </em>{' '}
        The AI references the name; the value stays encrypted, decrypted at runtime by your app.
      </p>
    ),
  },
  {
    q: "What's the difference between preview and live?",
    a: (
      <>
        <p>
          Preview is for iteration:{' '}
          <span className="font-mono text-xs">https://preview-{`{slug}`}.mkin.app</span>. Every
          deploy overwrites it. Nobody finds it unless you share the link.
        </p>
        <p>
          Live is what you share:{' '}
          <span className="font-mono text-xs">https://{`{slug}`}.mkin.app</span>. It only changes
          when you publish. Kin runs a health check before publishing; if it fails, live stays put
          and you get an email.
        </p>
        <p>
          Both share the same database (for backend apps). Schema changes hit live and preview
          together &mdash; test schema changes on a non-critical app first if you&apos;re nervous.
        </p>
      </>
    ),
  },
  {
    q: 'Why did my app roll back on its own?',
    a: (
      <p>
        After every publish, Kin makes test requests. If they fail (5xx, timeout, runtime
        exception), Kin assumes the deploy is broken and rolls live to the previous version. You get
        an email. Usually a code bug the AI introduced &mdash; tell it the symptom; it fixes and
        redeploys.
      </p>
    ),
  },
  {
    q: 'Can I edit the code directly without the AI?',
    a: (
      <p>
        Yes. Open the app and click <strong>File editor</strong> on the side nav &mdash; full Monaco
        editor with the workspace files, ⌘S to save, deploys to preview when you close the tab. Kin
        apps are plain JavaScript on Cloudflare Workers; a few Kin bindings; optional SQL migrations
        under <span className="font-mono text-xs">migrations/</span>. Hand edits and agent edits
        share the same workspace &mdash; just don&apos;t edit in both places at once.
      </p>
    ),
  },
  {
    q: 'What npm packages can I use?',
    a: (
      <p>
        None directly. Kin bundles for the Workers runtime &mdash; no node_modules resolution at
        deploy. The AI knows this and writes against the Workers primitives: the{' '}
        <span className="font-mono text-xs">fetch</span> global, D1 (SQLite), R2 (storage), secrets,
        Web Crypto, Web Streams. Enough for 95% of internal tools.
      </p>
    ),
  },
  {
    q: 'How do I roll back to an earlier version?',
    a: (
      <p>
        Two ways. <strong>(1)</strong> Ask the AI:{' '}
        <em>&ldquo;restore yesterday&apos;s version&rdquo;</em> or{' '}
        <em>&ldquo;roll back to version 4.&rdquo;</em> It picks the version and runs the restore;
        Kin keeps every published bundle. <strong>(2)</strong> Open the app&apos;s{' '}
        <strong>Versions</strong> tab and click <em>Restore</em>. Instant, no redeploy.
      </p>
    ),
  },
  {
    q: "Where can I see who's using my app?",
    a: (
      <p>
        The <strong>Usage</strong> tab on your app&apos;s portal page: page views and unique users
        over day / week / month / 90d, plus a per-user breakdown. Data from Cloudflare Analytics
        &mdash; no instrumentation needed in your app.
      </p>
    ),
  },
  {
    q: 'The agent is stuck mid-deploy. What now?',
    a: (
      <>
        <p>Tell it what you&apos;re seeing:</p>
        <p className="italic">
          &ldquo;The deploy is hanging &mdash; check health and tell me what state we&apos;re
          in.&rdquo;
        </p>
        <p>
          If the chat is confused about its own state, refresh the preview (in the browser) or start
          a fresh session (Claude Code). The <strong>Health</strong> tab in the portal shows the
          real state of preview and live &mdash; your app is almost certainly fine.
        </p>
      </>
    ),
  },
  {
    q: 'Is there a rate limit?',
    a: (
      <p>
        Soft yes. Kin runs on Cloudflare&apos;s generous limits. For Mytra-scale internal use (tens
        to hundreds of concurrent users, thousands of requests/day) you won&apos;t hit them.
        Anything that&apos;ll see outside-Mytra traffic (public form, webhook receiver), check with
        platform admins before publishing.
      </p>
    ),
  },
  {
    q: 'How do I call a Kin app from a script or Claude Code?',
    a: (
      <>
        <p>
          A manager opens specific paths to bearer-token access &mdash; one short prompt to the AI:{' '}
          <em>&ldquo;allow external access on /api and /mcp.&rdquo;</em> Until that happens, only
          the browser cookie works.
        </p>
        <p>
          Once paths are open, anyone with access to the app can mint a personal token on the
          app&apos;s page (or under the <strong>Tokens</strong> tab if you&apos;re a manager). The
          token is shown once at creation; copy it then. Use it as a bearer header against the
          allow-listed paths.
        </p>
      </>
    ),
  },
  {
    q: 'What is needs_database, and why did the AI ask?',
    a: (
      <>
        <p>
          The answer determines whether your app gets a SQLite database. Static lookups,
          calculators, info pages don&apos;t need one. Anything where one user&apos;s input is seen
          by others &mdash; sign-ups, forms, trackers &mdash; does. The skill usually picks right;
          you only see the question when it&apos;s genuinely ambiguous.
        </p>
        <p>
          Adding a database after the fact means deleting and re-creating. Get this right up front.
        </p>
      </>
    ),
  },
  {
    q: 'Can I delete an app?',
    a: (
      <p>
        Yes, managers only, from the app&apos;s <strong>Settings</strong> tab. Never via the AI
        &mdash; deletion wipes the database, assets, and routes, and the URL stops working
        immediately. We want a human click for that.
      </p>
    ),
  },
  {
    q: "I want to do something the agent says it can't. What now?",
    a: (
      <p>
        Possibilities: <strong>(1)</strong> Kin really doesn&apos;t support it yet (in flight:
        outside-Mytra access, logs viewer, screenshot testing). <strong>(2)</strong> If you&apos;re
        in Claude Code, your skill may be stale &mdash; re-run{' '}
        <span className="font-mono text-xs">/plugin install kin@mytra</span> from{' '}
        <a className="underline" href="/docs/getting-started">
          Getting started
        </a>
        . <strong>(3)</strong> Ask in <span className="font-mono text-xs">#kin</span>. Good chance
        someone has solved your shape.
      </p>
    ),
  },
];

function FaqItem({ q, a }: Q) {
  return (
    <details className="group border-b border-line last:border-b-0">
      <summary className="cursor-pointer list-none flex items-start gap-3 py-4 px-5 hover:bg-surface-2/50 transition-colors">
        <IconChevronRight
          size={16}
          className="mt-0.5 text-muted shrink-0 transition-transform group-open:rotate-90"
        />
        <span className="font-medium text-ink text-sm">{q}</span>
      </summary>
      <div className="px-5 pb-4 pl-12 text-sm text-ink-2 leading-relaxed space-y-2.5">{a}</div>
    </details>
  );
}

export function DocsFaq() {
  return (
    <div className="animate-fade-in">
      <PageHeader
        eyebrow="Docs · FAQ"
        title="Frequently asked questions"
        description="What people ask after their first or second app. If yours isn't here, try #kin on Slack."
      />

      <DocsTabs />

      <Card>
        <ul>
          {QUESTIONS.map((item) => (
            <li key={item.q}>
              <FaqItem {...item} />
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
